import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageSquare, Bell, Send, Users, Eye, Edit, Trash2, Plus,
  Search, Filter, Calendar, Clock, CheckCircle, XCircle,
  AlertCircle, Mail, Phone, MessageCircle, Megaphone,
  User, UserCheck, Settings, Download, Upload, Target
} from 'lucide-react';
import './NotificationManagement.css';

const NotificationManagement = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('send');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Notification form state
  const [notificationForm, setNotificationForm] = useState({
    type: 'email',
    title: '',
    message: '',
    recipients: 'all',
    targetRole: 'all',
    targetUsers: [],
    scheduledTime: '',
    template: '',
    priority: 'normal'
  });

  // Mock data - Replace with actual API calls
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: 'Welcome Message',
      type: 'email',
      subject: 'Welcome to InnBotics Hotel!',
      content: 'Welcome {{name}}! We\'re excited to have you as our guest. Your booking confirmation number is {{bookingId}}.',
      category: 'Guest',
      variables: ['name', 'bookingId'],
      created: '2024-10-01',
      lastUsed: '2024-10-10'
    },
    {
      id: 2,
      name: 'Booking Confirmation',
      type: 'email',
      subject: 'Booking Confirmed - {{hotelName}}',
      content: 'Dear {{guestName}}, your booking from {{checkIn}} to {{checkOut}} has been confirmed. Room: {{roomNumber}}.',
      category: 'Booking',
      variables: ['hotelName', 'guestName', 'checkIn', 'checkOut', 'roomNumber'],
      created: '2024-09-15',
      lastUsed: '2024-10-09'
    },
    {
      id: 3,
      name: 'Maintenance Alert',
      type: 'push',
      subject: 'Room Maintenance Required',
      content: 'Room {{roomNumber}} requires maintenance attention. Priority: {{priority}}',
      category: 'Staff',
      variables: ['roomNumber', 'priority'],
      created: '2024-09-20',
      lastUsed: '2024-10-08'
    },
    {
      id: 4,
      name: 'Payment Reminder',
      type: 'sms',
      subject: 'Payment Due',
      content: 'Hi {{name}}, payment of ${{amount}} is due for booking {{bookingId}}. Pay now: {{paymentLink}}',
      category: 'Billing',
      variables: ['name', 'amount', 'bookingId', 'paymentLink'],
      created: '2024-09-10',
      lastUsed: '2024-10-07'
    }
  ]);

  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'Welcome to InnBotics',
      type: 'email',
      recipients: 'specific',
      recipientCount: 15,
      targetRole: 'guest',
      status: 'sent',
      sentAt: '2024-10-10 10:30',
      scheduledAt: null,
      template: 'Welcome Message',
      priority: 'normal',
      deliveryRate: 93.3,
      openRate: 67.7,
      clickRate: 23.5,
      content: 'Welcome to our hotel! We hope you enjoy your stay.'
    },
    {
      id: 2,
      title: 'System Maintenance Notice',
      type: 'push',
      recipients: 'all',
      recipientCount: 45,
      targetRole: 'staff',
      status: 'scheduled',
      sentAt: null,
      scheduledAt: '2024-10-11 02:00',
      template: null,
      priority: 'high',
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      content: 'System maintenance scheduled for tonight from 2:00 AM to 4:00 AM.'
    },
    {
      id: 3,
      title: 'Payment Reminder',
      type: 'sms',
      recipients: 'specific',
      recipientCount: 8,
      targetRole: 'guest',
      status: 'delivered',
      sentAt: '2024-10-09 15:45',
      scheduledAt: null,
      template: 'Payment Reminder',
      priority: 'normal',
      deliveryRate: 100,
      openRate: 87.5,
      clickRate: 62.5,
      content: 'Payment reminder for upcoming booking.'
    },
    {
      id: 4,
      title: 'Room Cleaning Alert',
      type: 'push',
      recipients: 'role',
      recipientCount: 12,
      targetRole: 'staff',
      status: 'failed',
      sentAt: '2024-10-08 14:20',
      scheduledAt: null,
      template: 'Maintenance Alert',
      priority: 'urgent',
      deliveryRate: 25,
      openRate: 100,
      clickRate: 0,
      content: 'Urgent: Room 205 requires immediate cleaning attention.'
    }
  ]);

  const [recipients, setRecipients] = useState([
    { id: 1, name: 'All Users', type: 'all', count: 156 },
    { id: 2, name: 'All Guests', type: 'role', role: 'guest', count: 89 },
    { id: 3, name: 'All Staff', type: 'role', role: 'staff', count: 45 },
    { id: 4, name: 'Managers', type: 'role', role: 'manager', count: 12 },
    { id: 5, name: 'Admins', type: 'role', role: 'admin', count: 3 },
    { id: 6, name: 'Active Bookings', type: 'custom', filter: 'active_bookings', count: 34 },
    { id: 7, name: 'VIP Guests', type: 'custom', filter: 'vip_guests', count: 18 }
  ]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return '#27ae60';
      case 'delivered': return '#27ae60';
      case 'scheduled': return '#3498db';
      case 'failed': return '#e74c3c';
      case 'draft': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return CheckCircle;
      case 'delivered': return CheckCircle;
      case 'scheduled': return Clock;
      case 'failed': return XCircle;
      case 'draft': return Edit;
      default: return AlertCircle;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return Mail;
      case 'sms': return Phone;
      case 'push': return Bell;
      default: return MessageCircle;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#e74c3c';
      case 'high': return '#f39c12';
      case 'normal': return '#3498db';
      case 'low': return '#95a5a6';
      default: return '#95a5a6';
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesStatus = filterStatus === 'all' || notification.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || template.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleFormChange = (field, value) => {
    setNotificationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const loadTemplate = (template) => {
    setNotificationForm(prev => ({
      ...prev,
      title: template.subject,
      message: template.content,
      template: template.name,
      type: template.type
    }));
  };

  const sendNotification = () => {
    // Implement notification sending logic
    console.log('Sending notification:', notificationForm);
    
    // Add to notifications list
    const newNotification = {
      id: notifications.length + 1,
      title: notificationForm.title,
      type: notificationForm.type,
      recipients: notificationForm.recipients,
      recipientCount: getRecipientCount(),
      targetRole: notificationForm.targetRole,
      status: notificationForm.scheduledTime ? 'scheduled' : 'sent',
      sentAt: notificationForm.scheduledTime ? null : new Date().toISOString(),
      scheduledAt: notificationForm.scheduledTime,
      template: notificationForm.template,
      priority: notificationForm.priority,
      deliveryRate: 0,
      openRate: 0,
      clickRate: 0,
      content: notificationForm.message
    };

    setNotifications(prev => [newNotification, ...prev]);
    
    // Reset form
    setNotificationForm({
      type: 'email',
      title: '',
      message: '',
      recipients: 'all',
      targetRole: 'all',
      targetUsers: [],
      scheduledTime: '',
      template: '',
      priority: 'normal'
    });

    alert('Notification sent successfully!');
  };

  const getRecipientCount = () => {
    if (notificationForm.recipients === 'all') {
      return recipients.find(r => r.type === 'all')?.count || 0;
    } else if (notificationForm.recipients === 'role') {
      return recipients.find(r => r.role === notificationForm.targetRole)?.count || 0;
    } else {
      return notificationForm.targetUsers.length;
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedNotification(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotification(null);
    setModalType('');
  };

  const tabs = [
    { key: 'send', label: 'Send Notification', icon: Send },
    { key: 'history', label: 'Notification History', icon: Clock },
    { key: 'templates', label: 'Templates', icon: MessageSquare },
    { key: 'analytics', label: 'Analytics', icon: Target }
  ];

  return (
    <div className="notification-management">
      <div className="notification-management-header">
        <div className="header-content">
          <div className="header-left">
            <Bell size={28} />
            <div>
              <h1>Notification Management</h1>
              <p>Send notifications and manage communication with users</p>
            </div>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-outline"
              onClick={() => openModal('bulk-send')}
            >
              <Users size={16} />
              Bulk Send
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => setActiveTab('send')}
            >
              <Plus size={16} />
              New Notification
            </button>
          </div>
        </div>
      </div>

      <div className="notification-management-nav">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.key}
              className={`nav-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <IconComponent size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="notification-management-content">
        {activeTab === 'send' && (
          <div className="send-notification">
            <div className="form-section">
              <h3>Create New Notification</h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label>Notification Type</label>
                  <select
                    value={notificationForm.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="push">Push Notification</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={notificationForm.priority}
                    onChange={(e) => handleFormChange('priority', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Title/Subject</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder="Enter notification title..."
                  />
                </div>

                <div className="form-group full-width">
                  <label>Message Content</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => handleFormChange('message', e.target.value)}
                    placeholder="Enter your message..."
                    rows={6}
                  />
                </div>

                <div className="form-group">
                  <label>Recipients</label>
                  <select
                    value={notificationForm.recipients}
                    onChange={(e) => handleFormChange('recipients', e.target.value)}
                  >
                    <option value="all">All Users</option>
                    <option value="role">By Role</option>
                    <option value="specific">Specific Users</option>
                  </select>
                </div>

                {notificationForm.recipients === 'role' && (
                  <div className="form-group">
                    <label>Target Role</label>
                    <select
                      value={notificationForm.targetRole}
                      onChange={(e) => handleFormChange('targetRole', e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admins</option>
                      <option value="manager">Managers</option>
                      <option value="staff">Staff</option>
                      <option value="guest">Guests</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Schedule (Optional)</label>
                  <input
                    type="datetime-local"
                    value={notificationForm.scheduledTime}
                    onChange={(e) => handleFormChange('scheduledTime', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Use Template</label>
                  <select
                    value={notificationForm.template}
                    onChange={(e) => {
                      const template = templates.find(t => t.name === e.target.value);
                      if (template) {
                        loadTemplate(template);
                      }
                    }}
                  >
                    <option value="">No Template</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.name}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="recipient-preview">
                <h4>Recipients Preview</h4>
                <div className="recipient-count">
                  <Users size={16} />
                  <span>{getRecipientCount()} recipients will receive this notification</span>
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-outline">
                  Save as Draft
                </button>
                <button className="btn btn-outline">
                  Preview
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={sendNotification}
                  disabled={!notificationForm.title || !notificationForm.message}
                >
                  {notificationForm.scheduledTime ? 'Schedule' : 'Send'} Notification
                </button>
              </div>
            </div>

            <div className="quick-templates">
              <h4>Quick Templates</h4>
              <div className="template-cards">
                {templates.slice(0, 3).map(template => (
                  <div key={template.id} className="template-card">
                    <div className="template-header">
                      <span className="template-name">{template.name}</span>
                      <span className="template-type">{template.type}</span>
                    </div>
                    <p className="template-content">
                      {template.content.substring(0, 100)}...
                    </p>
                    <button 
                      className="btn btn-outline btn-sm"
                      onClick={() => loadTemplate(template)}
                    >
                      Use Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <>
            <div className="content-controls">
              <div className="search-bar">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-controls">
                <Filter size={16} />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="push">Push</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="notifications-table">
              <table>
                <thead>
                  <tr>
                    <th>Notification</th>
                    <th>Type</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Sent/Scheduled</th>
                    <th>Performance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.map(notification => {
                    const StatusIcon = getStatusIcon(notification.status);
                    const TypeIcon = getTypeIcon(notification.type);
                    return (
                      <tr key={notification.id}>
                        <td>
                          <div className="notification-cell">
                            <div className="notification-title">
                              <span 
                                className="priority-dot"
                                style={{ backgroundColor: getPriorityColor(notification.priority) }}
                              ></span>
                              <strong>{notification.title}</strong>
                            </div>
                            <small>{notification.content.substring(0, 60)}...</small>
                          </div>
                        </td>
                        <td>
                          <span className="type-badge">
                            <TypeIcon size={12} />
                            {notification.type}
                          </span>
                        </td>
                        <td>
                          <div className="recipients-cell">
                            <span>{notification.recipientCount} recipients</span>
                            <small>Role: {notification.targetRole}</small>
                          </div>
                        </td>
                        <td>
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(notification.status) }}
                          >
                            <StatusIcon size={12} />
                            {notification.status}
                          </span>
                        </td>
                        <td>
                          <div className="datetime-cell">
                            {notification.sentAt && (
                              <>
                                <div>{new Date(notification.sentAt).toLocaleDateString()}</div>
                                <small>{new Date(notification.sentAt).toLocaleTimeString()}</small>
                              </>
                            )}
                            {notification.scheduledAt && (
                              <>
                                <div>Scheduled</div>
                                <small>{new Date(notification.scheduledAt).toLocaleString()}</small>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          {notification.status === 'sent' || notification.status === 'delivered' ? (
                            <div className="performance-cell">
                              <small>Delivery: {notification.deliveryRate}%</small>
                              <small>Open: {notification.openRate}%</small>
                              <small>Click: {notification.clickRate}%</small>
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td>
                          <div className="notification-actions">
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('view', notification)}
                            >
                              <Eye size={14} />
                            </button>
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('edit', notification)}
                            >
                              <Edit size={14} />
                            </button>
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('delete', notification)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'templates' && (
          <>
            <div className="templates-header">
              <div className="content-controls">
                <div className="search-bar">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="filter-controls">
                  <Filter size={16} />
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="push">Push</option>
                  </select>
                </div>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => openModal('add-template')}
              >
                <Plus size={16} />
                New Template
              </button>
            </div>

            <div className="templates-grid">
              {filteredTemplates.map(template => {
                const TypeIcon = getTypeIcon(template.type);
                return (
                  <div key={template.id} className="template-card-large">
                    <div className="template-header">
                      <div className="template-info">
                        <h4>{template.name}</h4>
                        <span className="template-category">{template.category}</span>
                      </div>
                      <span className="template-type-badge">
                        <TypeIcon size={14} />
                        {template.type}
                      </span>
                    </div>
                    
                    <div className="template-content">
                      <h5>Subject: {template.subject}</h5>
                      <p>{template.content}</p>
                    </div>

                    <div className="template-variables">
                      <strong>Variables:</strong>
                      <div className="variables-list">
                        {template.variables.map(variable => (
                          <span key={variable} className="variable-tag">
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="template-footer">
                      <div className="template-stats">
                        <small>Created: {new Date(template.created).toLocaleDateString()}</small>
                        <small>Last used: {new Date(template.lastUsed).toLocaleDateString()}</small>
                      </div>
                      <div className="template-actions">
                        <button 
                          className="btn btn-outline btn-sm"
                          onClick={() => loadTemplate(template)}
                        >
                          Use Template
                        </button>
                        <button 
                          className="btn btn-outline btn-sm"
                          onClick={() => openModal('edit-template', template)}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => openModal('delete-template', template)}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <div className="analytics-summary">
              <div className="summary-cards">
                <div className="summary-card">
                  <div className="card-icon">
                    <Send size={24} />
                  </div>
                  <div className="card-content">
                    <h3>48</h3>
                    <p>Total Sent</p>
                    <small>+12% this week</small>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="card-content">
                    <h3>94.2%</h3>
                    <p>Delivery Rate</p>
                    <small>+2.3% this week</small>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon">
                    <Eye size={24} />
                  </div>
                  <div className="card-content">
                    <h3>68.5%</h3>
                    <p>Open Rate</p>
                    <small>-1.2% this week</small>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon">
                    <Target size={24} />
                  </div>
                  <div className="card-content">
                    <h3>24.8%</h3>
                    <p>Click Rate</p>
                    <small>+3.1% this week</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-details">
              <div className="performance-breakdown">
                <h4>Performance by Type</h4>
                <div className="breakdown-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sent</th>
                        <th>Delivered</th>
                        <th>Opened</th>
                        <th>Clicked</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Email</td>
                        <td>32</td>
                        <td>30 (93.8%)</td>
                        <td>22 (73.3%)</td>
                        <td>8 (36.4%)</td>
                      </tr>
                      <tr>
                        <td>SMS</td>
                        <td>12</td>
                        <td>12 (100%)</td>
                        <td>10 (83.3%)</td>
                        <td>7 (70%)</td>
                      </tr>
                      <tr>
                        <td>Push</td>
                        <td>4</td>
                        <td>3 (75%)</td>
                        <td>2 (66.7%)</td>
                        <td>1 (50%)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="recent-performance">
                <h4>Recent Performance</h4>
                <div className="performance-list">
                  {notifications.slice(0, 5).map(notification => (
                    <div key={notification.id} className="performance-item">
                      <div className="performance-info">
                        <strong>{notification.title}</strong>
                        <small>{notification.type} • {notification.recipientCount} recipients</small>
                      </div>
                      <div className="performance-metrics">
                        <span>Delivery: {notification.deliveryRate}%</span>
                        <span>Open: {notification.openRate}%</span>
                        <span>Click: {notification.clickRate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalType === 'view' && `Notification Details`}
                {modalType === 'edit' && `Edit Notification`}
                {modalType === 'delete' && `Delete Notification`}
                {modalType === 'add-template' && `New Template`}
                {modalType === 'edit-template' && `Edit Template`}
                {modalType === 'delete-template' && `Delete Template`}
                {modalType === 'bulk-send' && `Bulk Send Notification`}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {modalType === 'view' && selectedNotification && (
                <div className="notification-details">
                  <div className="detail-row">
                    <strong>Title:</strong> {selectedNotification.title}
                  </div>
                  <div className="detail-row">
                    <strong>Type:</strong> {selectedNotification.type}
                  </div>
                  <div className="detail-row">
                    <strong>Status:</strong> {selectedNotification.status}
                  </div>
                  <div className="detail-row">
                    <strong>Recipients:</strong> {selectedNotification.recipientCount}
                  </div>
                  <div className="detail-row">
                    <strong>Content:</strong>
                    <p>{selectedNotification.content}</p>
                  </div>
                  {selectedNotification.sentAt && (
                    <div className="detail-row">
                      <strong>Sent At:</strong> {new Date(selectedNotification.sentAt).toLocaleString()}
                    </div>
                  )}
                  {selectedNotification.scheduledAt && (
                    <div className="detail-row">
                      <strong>Scheduled At:</strong> {new Date(selectedNotification.scheduledAt).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              {modalType === 'delete' && (
                <div className="delete-confirmation">
                  <p>Are you sure you want to delete this notification?</p>
                  <p>This action cannot be undone.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {modalType === 'delete' && (
                <>
                  <button className="btn btn-danger">Delete</button>
                  <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
                </>
              )}
              {modalType !== 'delete' && (
                <button className="btn btn-outline" onClick={closeModal}>Close</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;